export interface AnalysisRequest {
  analysis_type: string;
  time_range: string;
  team_id?: string;
  player_ids?: string[];
  user_context?: string;
  formation?: string;
}

export interface AnalysisResponse {
  analysis: string;
  generated_at: string;
  data_summary: string;
}

export interface AnalysisType {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  supportsFormation: boolean;
  requiresPlayers: boolean;
}

export const ANALYSIS_TYPES: AnalysisType[] = [
  {
    id: 'injury_patterns',
    name: 'Injury Pattern Analysis',
    description: 'Identify injury patterns, risk factors, and prevention strategies',
    icon: '🛡️',
    color: 'text-red-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'team_wellness',
    name: 'Team Wellness Assessment',
    description: 'Analyze player wellness, recovery, and training load impact',
    icon: '❤️',
    color: 'text-pink-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'training_effectiveness',
    name: 'Training Effectiveness',
    description: 'Evaluate training session effectiveness and optimization opportunities',
    icon: '⚡',
    color: 'text-blue-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'team_morale',
    name: 'Team Morale & Culture',
    description: 'Assess team morale, mood patterns, and cultural indicators',
    icon: '👥',
    color: 'text-green-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'player_performance_trends',
    name: 'Player Performance Trends',
    description: 'Analyze individual player performance trajectories and development',
    icon: '📈',
    color: 'text-purple-600',
    supportsFormation: false,
    requiresPlayers: true
  },
  {
    id: 'team_composition',
    name: 'Team Composition Analysis',
    description: 'Analyze squad balance, formation suitability, and tactical options',
    icon: '🏃‍♂️',
    color: 'text-indigo-600',
    supportsFormation: true,
    requiresPlayers: false
  },
  {
    id: 'scouting_analysis',
    name: 'Scouting Reports Analysis',
    description: 'Identify best players, recruitment targets, and squad gaps',
    icon: '🔍',
    color: 'text-orange-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'goal_buildup_patterns',
    name: 'Goal Build-Up Patterns',
    description: 'Analyze common patterns in goal-scoring and goal-conceding possessions',
    icon: '⚽',
    color: 'text-emerald-600',
    supportsFormation: false,
    requiresPlayers: false
  },
  {
    id: 'match_stats_analysis',
    name: 'Match Stats Analysis',
    description: 'AI analysis of your last 10 matches using Wyscout advanced stats (xG, possession, passing, pressing, transitions)',
    icon: '📊',
    color: 'text-cyan-600',
    supportsFormation: false,
    requiresPlayers: false
  }
];

export const TIME_RANGES = [
  { id: 'week', name: 'Last Week' },
  { id: 'month', name: 'Last Month' },
  { id: 'season', name: 'Season (6 months)' }
];

export const FORMATIONS = [
  { id: '', name: 'No specific formation' },
  { id: '4-4-2', name: '4-4-2' },
  { id: '4-3-3', name: '4-3-3' },
  { id: '4-2-3-1', name: '4-2-3-1' },
  { id: '3-5-2', name: '3-5-2' },
  { id: '4-1-4-1', name: '4-1-4-1' },
  { id: '3-4-3', name: '3-4-3' },
  { id: '5-3-2', name: '5-3-2' }
];

export const CONTEXT_TEMPLATES = [
  { id: 'defensive_focus', text: 'Focus on defensive weaknesses and improvements needed' },
  { id: 'attacking_options', text: 'Analyze attacking options and creative players for the next transfer window' },
  { id: 'youth_development', text: 'Identify young players with high development potential' },
  { id: 'squad_depth', text: 'Assess squad depth and identify positions needing backup players' },
  { id: 'injury_prone', text: 'Highlight injury-prone players and fitness concerns' },
  { id: 'tactical_versatility', text: 'Find players who offer tactical versatility and can play multiple positions' },
  { id: 'leadership', text: 'Identify leadership qualities and mentality in players' },
  { id: 'pace_power', text: 'Focus on pace and physical attributes for counter-attacking play' }
];
